import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Search, GraduationCap, Users, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  if (clean.length < 2) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv.toUpperCase()}`;
};

const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

export default function EseMatriz() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState({ capacitaciones: [], trabajadores: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCap, setFilterCap] = useState('all'); // 'all' | 'pendientes' | 'aprobadas'

  const fetchMatriz = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ese/matriz-capacitaciones`, { headers });
      setData(res.data);
    } catch { toast.error('Error al cargar la matriz'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { fetchMatriz(); }, [fetchMatriz]);

  const trabajadoresFiltered = data.trabajadores.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    const cleanQ = q.replace(/[.\-]/g, '');
    return (t.nombre_completo || '').toLowerCase().includes(q)
      || (t.rut || '').replace(/[.\-]/g, '').toLowerCase().includes(cleanQ)
      || (t.email || '').toLowerCase().includes(q);
  });

  const capacitacionesFiltered = filterCap === 'pendientes'
    ? data.capacitaciones.filter(c => trabajadoresFiltered.some(t => t.capacitaciones[c.id] === 'pendiente'))
    : filterCap === 'aprobadas'
      ? data.capacitaciones.filter(c => trabajadoresFiltered.some(t => t.capacitaciones[c.id] === 'aprobado'))
      : data.capacitaciones;

  // Stats globales
  const totalAsignaciones = data.trabajadores.reduce((acc, t) => acc + t.total, 0);
  const totalAprobadas = data.trabajadores.reduce((acc, t) => acc + t.aprobadas, 0);
  const totalPendientes = totalAsignaciones - totalAprobadas;
  const cumplimientoPromedio = data.trabajadores.length
    ? Math.round(data.trabajadores.reduce((acc, t) => acc + t.porcentaje, 0) / data.trabajadores.length)
    : 0;
  const trabajadoresAl100 = data.trabajadores.filter(t => t.total > 0 && t.porcentaje === 100).length;

  return (
    <div className="space-y-6" data-testid="ese-matriz">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Matriz de Capacitaciones</h1>
        <p className="text-sm text-slate-500 mt-1">
          Vista cruzada: trabajadores vs capacitaciones asignadas. Te permite identificar quién está al 100% y quién aún tiene pendientes.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Trabajadores" value={data.trabajadores.length} icon={Users} color="blue" />
        <StatCard label="Cumplimiento promedio" value={`${cumplimientoPromedio}%`} icon={GraduationCap} color="indigo" suffix={`${trabajadoresAl100} al 100%`} />
        <StatCard label="Aprobadas" value={totalAprobadas} icon={CheckCircle2} color="green" suffix={`de ${totalAsignaciones}`} />
        <StatCard label="Pendientes" value={totalPendientes} icon={AlertCircle} color="amber" />
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, RUT o email..."
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="search-matriz" />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {[
            { id: 'all', label: 'Todas las capacitaciones' },
            { id: 'pendientes', label: 'Solo pendientes' },
            { id: 'aprobadas', label: 'Solo aprobadas' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilterCap(f.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterCap === f.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Matriz */}
      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-400">Cargando matriz...</div>
      ) : data.trabajadores.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Users className="w-12 h-12 mx-auto text-slate-300" />
          <p className="text-sm text-slate-500 mt-3">No tienes trabajadores registrados aun.</p>
          <p className="text-xs text-slate-400 mt-1">Cuando agregues trabajadores y les asignes areas/actividades, aqui veras su progreso.</p>
        </div>
      ) : capacitacionesFiltered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <GraduationCap className="w-12 h-12 mx-auto text-slate-300" />
          <p className="text-sm text-slate-500 mt-3">No hay capacitaciones que coincidan con el filtro.</p>
          <p className="text-xs text-slate-400 mt-1">Verifica que tus trabajadores tengan areas y actividades asignadas.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-slate-50">
                <tr>
                  <th className="sticky left-0 bg-slate-50 z-10 text-left px-3 py-3 border-r border-slate-200 w-[160px] min-w-[160px]">
                    <span className="text-slate-500 font-medium">Trabajador</span>
                  </th>
                  <th className="px-2 py-3 text-center border-r border-slate-200 w-[80px] min-w-[80px]">
                    <span className="text-slate-500 font-medium">Cumplimiento</span>
                  </th>
                  {capacitacionesFiltered.map(c => (
                    <th key={c.id} className="px-1.5 py-2 border-r border-slate-100 w-[110px] min-w-[110px] max-w-[110px] align-bottom"
                        title={c.nombre}>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-[10px] text-slate-700 font-medium leading-tight text-center break-words"
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {c.nombre}
                        </span>
                        {c.obligatoria && (
                          <span className="text-[8px] font-bold text-red-500 uppercase tracking-wide">Obligatoria</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trabajadoresFiltered.map(t => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`row-${t.id}`}>
                    <td className="sticky left-0 bg-white z-10 px-3 py-3 border-r border-slate-200 w-[160px] min-w-[160px]">
                      <div className="font-medium text-slate-900 text-xs truncate" title={toTitleCase(t.nombre_completo)}>{toTitleCase(t.nombre_completo)}</div>
                      <div className="font-mono text-[10px] text-slate-400">{formatRut(t.rut)}</div>
                    </td>
                    <td className="px-2 py-3 text-center border-r border-slate-200 w-[80px] min-w-[80px]">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`text-sm font-bold ${t.porcentaje === 100 ? 'text-green-600' : t.porcentaje >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                          {t.porcentaje}%
                        </span>
                        <span className="text-[10px] text-slate-400">{t.aprobadas}/{t.total}</span>
                        <div className="w-12 h-1 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full ${t.porcentaje === 100 ? 'bg-green-500' : t.porcentaje >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                               style={{ width: `${t.porcentaje}%` }} />
                        </div>
                      </div>
                    </td>
                    {capacitacionesFiltered.map(c => {
                      const estado = t.capacitaciones[c.id];
                      return (
                        <td key={c.id} className="px-2 py-3 text-center border-r border-slate-100 w-[110px] min-w-[110px]"
                            title={`${c.nombre}: ${estado || 'No aplica'}`}>
                          {estado === 'aprobado' && <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" />}
                          {estado === 'pendiente' && <Circle className="w-4 h-4 text-amber-500 mx-auto" />}
                          {!estado && <span className="text-slate-200 text-[10px]">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Leyenda */}
          <div className="flex items-center gap-4 px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-[10px] text-slate-600">
            <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Aprobado</span>
            <span className="flex items-center gap-1"><Circle className="w-3 h-3 text-amber-500" /> Pendiente</span>
            <span className="flex items-center gap-1"><span className="text-slate-300">—</span> No aplica</span>
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, color, suffix }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color]}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <p className="text-2xl font-bold mt-2">{value}</p>
      {suffix && <p className="text-[10px] opacity-70 mt-1">{suffix}</p>}
    </div>
  );
};
